/**
 * Monta a definição do relatório do dia para o pdfmake (issue #relatorio-do-dia).
 *
 * É função pura: recebe o payload de `GET /relatorio-dia` e devolve a
 * docDefinition. Reaproveita a linguagem visual do relatório de abordagem
 * (Abordagens/Imprimir/relatorio/montaDocumento.js), mas é um CONSOLIDADO:
 * capa com totais do dia + um bloco compacto por abordagem, cada uma com
 * suas ocorrências aninhadas — não o dossiê completo de uma abordagem.
 */

import { dataHora, quantidadeDoItem } from '../../Abordagens/Imprimir/relatorio/formatadores';

// ---------------------------------------------------------------- paleta
// mesma paleta do relatório de abordagem: mantém a linguagem visual do
// documento oficial (cabeçalho, marca d'água, rodapé de emissão).
const TINTA = '#1c2b36';
const TINTA_FRACA = '#6b7c88';
const LINHA = '#d4dce2';
const FUNDO_SECAO = '#eef2f5';
const FUNDO_CAIXA = '#f6f8f9';
const DESTRUTIVA = '#d32f2f'; // --sig-destrutiva (cores-sobrias.css)
const NAT_POLICIAL = '#5f6b1f'; // oliva da tela, escurecido para impressão
const NAT_FISCAL = '#7a1f42'; // bordô da tela, escurecido para impressão

const PADRAO = { marca: { texto: 'OPERAÇÃO FOCO' } };

/**
 * Marca d'água como BACKGROUND, e não pelo `watermark` do pdfmake: o
 * watermark é desenhado POR CIMA do conteúdo — inaceitável num relatório
 * que serve de prova. Como background, ela fica atrás de tudo.
 */
const marcaDaguaSVG = ({ texto, cor = TINTA, opacidade = 0.06, tamanho = 54 }) => {
  const escapado = String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;');
  return (
    `<svg width="595" height="842" xmlns="http://www.w3.org/2000/svg">` +
    `<text x="297" y="421" text-anchor="middle" transform="rotate(-45 297 421)" ` +
    `font-family="Helvetica" font-weight="bold" font-size="${tamanho}" ` +
    `fill="${cor}" fill-opacity="${opacidade}">${escapado}</text></svg>`
  );
};

// `data` do payload é uma data pura ('AAAA-MM-DD'), sem horário. Fixa o
// horário em meio-dia no fuso de Brasília antes de formatar para nunca
// escorregar de dia por causa de fuso.
const soData = iso => dataHora(`${iso}T12:00:00.000-03:00`).split(' ')[0];
// `dataHora` devolve '—' (sem espaço) quando o ISO é falsy — sem o fallback
// abaixo, `[1]` vira `undefined` e vaza como string literal no documento.
const soHora = iso => dataHora(iso).split(' ')[1] || '—';

// ---------------------------------------------------------------- blocos
// título de seção: barra clara com filete escuro embaixo
const secao = texto => ({
  table: { widths: ['*'], body: [[{ text: texto, style: 'secao' }]] },
  layout: {
    hLineWidth: (i, node) => (i === node.table.body.length ? 1 : 0),
    vLineWidth: () => 0,
    hLineColor: () => TINTA,
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 3,
    fillColor: () => FUNDO_SECAO,
  },
  margin: [0, 14, 0, 6],
});

// campo rotulado: rótulo miúdo em cima, valor embaixo
const campo = (rotulo, valor, opcoes = {}) => ({
  stack: [
    { text: String(rotulo).toUpperCase(), style: 'rotulo' },
    {
      text: valor === null || valor === undefined || valor === '' ? '—' : String(valor),
      style: 'valor',
      color: opcoes.cor || TINTA,
    },
  ],
  margin: [0, 0, 0, 6],
});

// grade de campos em N colunas, completando a última linha
const grade = (campos, colunas = 3) => {
  const linhas = [];
  for (let i = 0; i < campos.length; i += colunas) {
    const linha = campos.slice(i, i + colunas);
    while (linha.length < colunas) linha.push({ text: '' });
    linhas.push(linha);
  }
  return { table: { widths: Array(colunas).fill('*'), body: linhas }, layout: 'noBorders' };
};

const vazio = texto => ({ text: texto, style: 'vazio', margin: [0, 2, 0, 6] });

// caixa cinza-clara com filete embaixo: usada para o corpo das abordagens e ocorrências
const caixa = conteudo => ({
  table: { widths: ['*'], body: [[{ stack: conteudo }]] },
  layout: {
    hLineWidth: (i, node) => (i === node.table.body.length ? 0.5 : 0),
    vLineWidth: () => 0,
    hLineColor: () => LINHA,
    fillColor: () => FUNDO_CAIXA,
    paddingLeft: () => 8,
    paddingRight: () => 8,
    paddingTop: () => 6,
    paddingBottom: () => 6,
  },
});

// faixa colorida de destaque (título de bloco), no padrão do relatório de abordagem
const faixaColorida = (esquerda, direita, cor) => ({
  table: {
    widths: ['*', 'auto'],
    body: [[
      { text: esquerda, style: 'faixa' },
      { text: direita, style: 'faixa', alignment: 'right' },
    ]],
  },
  layout: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    fillColor: () => cor,
    paddingLeft: () => 8,
    paddingRight: () => 8,
    paddingTop: () => 5,
    paddingBottom: () => 5,
  },
  margin: [0, 10, 0, 0],
});

// ---------------------------------------------------------------- ocorrência
function blocoOcorrencia(oco) {
  const fiscal = Number(oco.natureza) !== 2; // 1 = fiscal (padrão), 2 = policial
  const cor = fiscal ? NAT_FISCAL : NAT_POLICIAL;
  const rotuloNatureza = fiscal ? 'FISCAL' : 'POLICIAL';

  const itens = (oco.itens || [])
    .map(it => {
      const quantidade = quantidadeDoItem({ quantidade: it.quantidade, unidade_medida: { nome: it.unidade } });
      return `${it.item || '—'}: ${quantidade}`;
    })
    .join('; ');

  const dinamica = (oco.dinamicas || [])
    .map(d => d.dinamica)
    .filter(Boolean)
    .join(' ');

  return {
    unbreakable: true,
    stack: [
      faixaColorida(
        `OCORRÊNCIA Nº ${oco.id}`,
        `${rotuloNatureza}   ·   ${oco.autuado ? 'AUTUADO' : 'NÃO AUTUADO'}`,
        cor
      ),
      caixa([
        { text: oco.razao || '—', style: 'razao' },
        campo('Registro de ocorrência (R.O.)', oco.ro || 'Não informado', { cor: oco.ro ? TINTA : DESTRUTIVA }),
        itens ? campo('Itens', itens) : vazio('Nenhum item lançado.'),
        { text: 'DINÂMICA', style: 'rotulo', margin: [0, 4, 0, 1] },
        { text: dinamica || 'Sem descrição da dinâmica.', style: dinamica ? 'corrido' : 'vazio' },
      ]),
    ],
    margin: [0, 0, 0, 2],
  };
}

// ---------------------------------------------------------------- abordagem
// `mostrarPosto`: só true no consolidado (abrangencia === 'todos') — com um
// posto só, repetir a sigla em cada um dos blocos seria ruído puro.
function blocoAbordagem(a, mostrarPosto = false) {
  const placas = (a.veiculos || [])
    .map(v => [v.placa, v.chassi].filter(Boolean).join(' / '))
    .filter(Boolean)
    .join(', ');

  const abordados = (a.abordados || [])
    .map(p => `${p.nome} — CPF ${p.cpf || 'não informado'} (${p.tipo || '—'})`)
    .join('; ');

  const empresas = (a.empresas || [])
    .map(e => `${e.nome || 'Empresa sem nome'} — CNPJ ${e.cnpj}`)
    .join('; ');

  const ocorrencias = (a.ocorrencias || []).map(blocoOcorrencia);

  // Evasão só entra quando verdadeira: um marcador curto e vermelho junto do
  // número/hora/ala — "Evasão: Não" em 208 blocos seria só ruído.
  const cabecalho = mostrarPosto && a.posto ? [a.posto, soHora(a.dt_h_inicio), a.ala || '—'] : [soHora(a.dt_h_inicio), a.ala || '—'];
  const direita = [cabecalho.join('   ·   ')];
  if (a.evasao) direita.push({ text: '   ·   EVASÃO', color: DESTRUTIVA, bold: true });

  return {
    unbreakable: false, // um dia inteiro não pode virar um único bloco intransponível
    stack: [
      faixaColorida(`ABORDAGEM Nº ${a.id}`, direita, TINTA),
      caixa([
        grade(
          [
            campo('Veículos', placas || '—'),
            campo('Abordados', abordados || '—'),
            campo('Empresas', empresas || '—'),
          ],
          1
        ),
      ]),
      ...ocorrencias,
    ],
    margin: [0, 0, 0, 8],
  };
}

// ---------------------------------------------------------------- documento
export default function montaDocumentoDia(dados, entrada = {}) {
  const opcoes = { ...PADRAO, ...entrada };
  const { posto, data, totais, abordagens, abrangencia } = dados;
  const emitidoEm = opcoes.emitidoEm || '';
  const emitidoPor = opcoes.emitidoPor || '';
  const dataFormatada = soData(data);
  // Posto sem sigla cadastrada não pode virar "undefined" no título — mesma
  // lógica de fallback que o helper `campo()` já aplica no resto do documento.
  const siglaPosto = posto.sigla || posto.nome || '—';
  // Task 11 (D7): abrangência 'todos' consolida a gerência-mãe (`posto` já
  // vem como a mãe, do backend) com TODAS as subordinadas — a faixa precisa
  // dizer isso, ou parece um relatório só da mãe.
  const tituloDia =
    abrangencia === 'todos'
      ? `${siglaPosto} · TODOS OS POSTOS · ${dataFormatada}`
      : `${siglaPosto} · ${dataFormatada}`;

  // plural de "abordagem" não é "+S" (troca o "M" por "NS") — daí a palavra
  // inteira no ternário, em vez de um sufixo.
  const totalAbordagens = (abordagens || []).length;
  const rotuloAbordagens = `${totalAbordagens} ${totalAbordagens === 1 ? 'ABORDAGEM' : 'ABORDAGENS'}`;

  const resumo = [
    campo('Abordagens', totais.abordagens),
    campo('Ocorrências', totais.ocorrencias),
    campo('Fiscais', totais.fiscais),
    campo('Policiais', totais.policiais),
    campo('Autuados', totais.autuados),
    campo('Itens', totais.itens),
  ];

  return {
    pageSize: 'A4',
    pageMargins: [40, 96, 40, 48],
    info: {
      title: `Relatório do Dia — ${tituloDia}`,
      author: 'SIG — Operação Foco',
      subject: 'Relatório do dia',
    },

    background: opcoes.marca
      ? () => ({ svg: marcaDaguaSVG(opcoes.marca), width: 595, absolutePosition: { x: 0, y: 0 } })
      : undefined,

    header: pagina =>
      pagina === 1
        ? {
            stack: [
              opcoes.brasao
                ? { image: opcoes.brasao, width: 38, alignment: 'center', margin: [0, 18, 0, 4] }
                : { text: '', margin: [0, 30, 0, 0] },
              { text: 'GOVERNO DO ESTADO DO RIO DE JANEIRO', style: 'orgao' },
              // A Operação Foco integra o Gabinete de Segurança Institucional
              // (agosto/2026); não está mais sob a Casa Civil.
              { text: 'Gabinete de Segurança Institucional  ·  Operação Foco', style: 'orgaoFraco' },
            ],
            margin: [40, 0, 40, 0],
          }
        : {
            margin: [40, 60, 40, 0],
            columns: [
              { text: `Relatório do Dia · ${tituloDia}`, style: 'cabecalhoCorrido' },
              { text: 'SIG · Operação Foco', style: 'cabecalhoCorrido', alignment: 'right' },
            ],
          },

    // Rodapé de emissão: identifica o posto, o dia, quem tirou e quando — e
    // serve de rastro se a folha for impressa e circular solta. A numeração
    // NÃO sai aqui: é carimbada no gerador final (Task 9), depois que este
    // documento já está fechado.
    footer: () => ({
      margin: [40, 8, 40, 0],
      stack: [
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: LINHA }] },
        {
          columns: [
            {
              text: [
                { text: tituloDia, bold: true },
                `   ·   Emitido em ${emitidoEm} por ${emitidoPor}`,
              ],
              style: 'rodape',
            },
            { text: '', width: 70 },
          ],
          margin: [0, 4, 0, 0],
        },
      ],
    }),

    content: [
      { text: 'RELATÓRIO DO DIA', style: 'titulo' },
      {
        table: {
          widths: ['*', 'auto'],
          body: [[
            { text: tituloDia.toUpperCase(), style: 'faixaTitulo' },
            { text: rotuloAbordagens, style: 'faixaTitulo', alignment: 'right' },
          ]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          fillColor: () => TINTA,
          paddingLeft: () => 8,
          paddingRight: () => 8,
          paddingTop: () => 6,
          paddingBottom: () => 6,
        },
        margin: [0, 6, 0, 10],
      },
      secao('Resumo do dia'),
      grade(resumo, 3),
      ...(abordagens && abordagens.length
        ? [secao('Abordagens'), ...abordagens.map(a => blocoAbordagem(a, abrangencia === 'todos'))]
        : [secao('Abordagens'), vazio('Nenhuma abordagem registrada neste dia.')]),
    ],

    styles: {
      titulo: {
        fontSize: 15,
        bold: true,
        alignment: 'center',
        color: TINTA,
        characterSpacing: 1.2,
        margin: [0, 4, 0, 0],
      },
      faixaTitulo: { fontSize: 10, bold: true, color: '#ffffff', characterSpacing: 0.6 },
      faixa: { fontSize: 9.5, bold: true, color: '#ffffff', characterSpacing: 0.5 },
      orgao: { fontSize: 8.5, bold: true, alignment: 'center', color: TINTA, characterSpacing: 0.5 },
      orgaoFraco: { fontSize: 8, alignment: 'center', color: TINTA_FRACA },
      cabecalhoCorrido: { fontSize: 7.5, color: TINTA_FRACA },
      secao: { fontSize: 9, bold: true, color: TINTA, characterSpacing: 0.8 },
      rotulo: { fontSize: 6.5, color: TINTA_FRACA, characterSpacing: 0.5, bold: true },
      valor: { fontSize: 9.5 },
      razao: { fontSize: 9.5, bold: true, color: TINTA, margin: [0, 0, 0, 4] },
      corrido: { fontSize: 8.5, alignment: 'justify', lineHeight: 1.2, color: TINTA },
      vazio: { fontSize: 8.5, italics: true, color: TINTA_FRACA },
      rodape: { fontSize: 7, color: TINTA_FRACA },
    },
    defaultStyle: { font: 'Roboto', fontSize: 9, color: TINTA },
  };
}
